import { api, ApiError, type AppState, type Member } from './api.js';
import { ce, el, clear, avatarEl } from './dom.js';
import { langSwitcher, t } from './i18n.js';
import { icons } from './icons.js';
import { renderSetup } from './views/setup.js';
import { renderPicker } from './views/picker.js';
import { renderDisplay } from './views/display.js';
import { renderTasks } from './views/tasks.js';
import { renderToday } from './views/today.js';
import { renderWallet } from './views/wallet.js';
import { renderBoard } from './views/board.js';
import {
  renderAdminShell,
  renderAdminSection,
  ADMIN_SECTION_NAMES,
  DEFAULT_ADMIN_SECTION,
} from './views/admin.js';
import { renderTemplates } from './views/templates.js';
import { Router, type Route } from '@vaadin/router';
// Material 3 web components used by the app shell (ADR-0051). Importing a
// module registers its custom element; only what's imported is bundled.
import '@material/web/iconbutton/icon-button.js';
import '@material/web/list/list.js';
import '@material/web/list/list-item.js';
import '@material/web/ripple/ripple.js';

const app = document.getElementById('app') as HTMLElement;
type TabName = 'tasks' | 'today' | 'wallet' | 'board' | 'templates' | 'admin';

async function boot(tab: TabName = 'tasks'): Promise<void> {
  // Only the main screen carries the top app bar; picker / setup / display /
  // access screens keep the plain padded #app.
  app.classList.remove('has-appbar');
  let state: AppState;
  try {
    state = await api.get<AppState>('/api/state');
  } catch (err) {
    if (err instanceof ApiError && err.status === 401 && err.body.code === 'household-access-required') {
      showAccessRequired();
      return;
    }
    throw err;
  }
  if (!state.setup) {
    clear(app);
    // The wizard may hand off straight to Admin (ADR-0024).
    app.append(renderSetup((_member, dest) => void boot(dest ?? 'tasks')));
    return;
  }
  try {
    const session = await api.get<{ member?: Member; display?: boolean }>('/api/session');
    if (session.member) showMain(session.member, '/' + tab);
    else if (session.display) showDisplay();
    else showPicker(state);
  } catch {
    showPicker(state);
  }
}

function showAccessRequired(): void {
  clear(app);
  app.append(
    el(
      'div',
      { class: 'card' },
      el('div', { class: 'spread' }, el('h1', {}, t('accessRequiredTitle')), langSwitcher()),
      el('p', { class: 'muted' }, t('accessRequiredBody')),
    ),
  );
}

function showPicker(state: AppState): void {
  clear(app);
  app.append(
    renderPicker(
      state.members ?? [],
      (member) => showMain(member),
      // Entering the shared display (ADR-0049): a device session, not a member.
      () => void api.post('/api/session/display').then(() => showDisplay()),
    ),
  );
}

// The shared-display screen (ADR-0049): no header menu, no tabs — just the
// task list with the two-tap attribute-on-complete flow, and a way back to
// the picker.
function showDisplay(): void {
  clear(app);
  app.append(renderDisplay(() => void api.del('/api/session').then(() => boot())));
}

/**
 * The header: who you are, and everything session-shaped behind one tap
 * (ADR-0047).
 *
 * Language and "switch profile" used to sit in the header of every screen,
 * permanently — chrome for rare actions in an app the family opens many
 * times a day. They live in this menu now, and the trigger is the avatar
 * alone (human-confirmed 2026-07-24). The active member's name is not shown
 * visually, so the trigger's aria-label carries it — a screen-reader user
 * still hears who is signed in — and the menu itself repeats it at the top.
 */
function profileMenu(member: Member): HTMLElement {
  const dialog = el('dialog', { class: 'profile-menu', id: 'profile-menu' });
  dialog.append(
    el(
      'div',
      { class: 'profile-menu-head' },
      avatarEl(member.name, member.avatar, member.color, 32),
      el('strong', {}, member.name),
    ),
    el('p', { class: 'muted task-menu-section-label' }, t('language')),
    langSwitcher(),
    el('hr', { class: 'menu-divider' }),
    el(
      'button',
      {
        class: 'menu-item',
        type: 'button',
        id: 'switch-profile',
        onclick: () => {
          dialog.close();
          void api.del('/api/session').then(() => boot());
        },
      },
      t('switchProfile'),
    ),
  );
  // Clicking the backdrop closes it; the dialog itself swallows the click.
  dialog.addEventListener('click', (ev) => {
    if (ev.target === dialog) dialog.close();
  });

  const trigger = el(
    'button',
    {
      class: 'profile-trigger',
      type: 'button',
      id: 'profile-menu-open',
      'aria-haspopup': 'dialog',
      // Name is not shown, so it goes in the accessible name: "Kim — your
      // profile and settings".
      'aria-label': `${member.name} — ${t('profileMenu')}`,
      onclick: () => dialog.showModal(),
    },
    avatarEl(member.name, member.avatar, member.color, 32),
    icons.chevronDown(),
  );

  return el('div', { class: 'row' }, trigger, dialog);
}

interface RouteDef {
  name: TabName;
  title: () => string;
  /**
   * Fills the route's OWN element rather than returning one of its own. Admin
   * needs that shape — its sections mount as nested routes into that same
   * element, and its sticky tab row has to share a parent with them — so every
   * route uses it, for one idiom instead of two.
   */
  mount: (host: HTMLElement) => void;
  adminOnly?: boolean;
}

const ROUTES: RouteDef[] = [
  { name: 'tasks', title: () => t('tasksTitle'), mount: (host) => host.append(renderTasks()) },
  { name: 'today', title: () => t('todayTitle'), mount: (host) => host.append(renderToday()) },
  { name: 'wallet', title: () => t('walletTitle'), mount: (host) => host.append(renderWallet()) },
  { name: 'board', title: () => t('boardTitle'), mount: (host) => host.append(renderBoard()) },
  { name: 'templates', title: () => t('navTemplates'), mount: (host) => host.append(renderTemplates()), adminOnly: true },
  // Admin is a parent route: it mounts the shell (attention strip + section
  // tabs) and its `/admin/<section>` children mount below it (ADR-0052).
  { name: 'admin', title: () => t('navAdmin'), mount: renderAdminShell, adminOnly: true },
];

/**
 * The app shell (ADR-0051): an M3 top app bar (hamburger · centered screen
 * title · avatar) plus a navigation drawer, over a Vaadin Router outlet. Each
 * route mounts the EXISTING plain-DOM view unchanged — only the shell is
 * Material for now; components migrate later. `@material/web` ships no
 * top-app-bar, so the bar itself is built to M3 spec with the theme tokens.
 */
function showMain(member: Member, initialPath = '/tasks'): void {
  clear(app);
  app.classList.add('has-appbar');
  const visible = ROUTES.filter((r) => !r.adminOnly || member.role === 'admin');

  // Navigation drawer (hamburger target). Native <dialog> for modality +
  // Esc-close; Material list items inside.
  const drawer = el('dialog', { class: 'nav-drawer', id: 'nav-drawer' }) as HTMLDialogElement;
  const list = ce('md-list');
  const itemByName = new Map<TabName, HTMLElement>();
  for (const r of visible) {
    const item = ce('md-list-item', { type: 'button', 'data-nav': r.name }, r.title());
    item.addEventListener('click', () => {
      drawer.close();
      void Router.go('/' + r.name);
    });
    itemByName.set(r.name, item);
    list.append(item);
  }
  drawer.append(el('div', { class: 'nav-drawer-head' }, el('strong', {}, t('navMenu'))), list);
  drawer.addEventListener('click', (ev) => {
    if (ev.target === drawer) drawer.close();
  });

  // Top app bar. No md-top-app-bar exists, so it's a themed <header>; the
  // hamburger is a real md-icon-button (slotting our inline SVG, since the
  // Material Symbols font is CDN-only and the privacy rule forbids it).
  // The bar's title IS the screen's heading (ADR-0051 Stage 4): views no
  // longer print their own <h1> under it, so this is the page's single h1.
  const title = el('h1', { class: 'app-bar-title' });
  const hamburger = ce('md-icon-button', { class: 'app-bar-menu', 'aria-label': t('navMenu') }, icons.menu());
  hamburger.addEventListener('click', () => drawer.showModal());
  // The bar's background spans the full viewport; its content is centered to
  // the same max-width column as the outlet, so on a wide desktop the
  // hamburger/title/avatar stay put while the bar itself goes edge to edge.
  const bar = el(
    'header',
    { class: 'app-bar' },
    el('div', { class: 'app-bar-inner' }, hamburger, title, el('div', { class: 'app-bar-trailing' }, profileMenu(member))),
  );

  const outlet = el('main', { id: 'route-outlet', class: 'route-outlet' });
  app.append(bar, outlet, drawer);

  // Land on the initial route by REPLACING the root entry, so Back from the
  // landing screen exits main (to the picker) rather than looping on a
  // redirect. A deep link (reload on /wallet) keeps its own path.
  if (location.pathname === '/' || location.pathname === '') {
    history.replaceState(history.state, '', initialPath);
  }

  // Admin's shell has to outlive a section change (ADR-0052). The router
  // re-runs a parent route's action on every child navigation and only reuses
  // what it gets back by IDENTITY (it did not create the element, so it cannot
  // match by tag name) — so hand back the same wrapper each time, and rebuild
  // its contents only when Admin is entered afresh, which keeps the attention
  // strip from going stale while you were on another screen.
  const adminView = el('div', { class: 'view' });
  let adminMounted = false;
  function adminShellView(): HTMLElement {
    if (!adminMounted) {
      clear(adminView);
      renderAdminShell(adminView);
      adminMounted = true;
    }
    return adminView;
  }

  const router = new Router(outlet);
  void router.setRoutes([
    { path: '/', redirect: '/tasks' },
    ...ROUTES.map((r): Route => ({
      path: '/' + r.name,
      action: (_context, commands) => {
        if (r.adminOnly && member.role !== 'admin') return commands.redirect('/tasks');
        if (r.name === 'admin') return adminShellView();
        const wrap = el('div', { class: 'view' });
        r.mount(wrap);
        return wrap;
      },
      // The router appends a child route's element to its parent's element, so
      // each section lands directly under the admin shell inside the same
      // `.view` wrapper.
      ...(r.name === 'admin'
        ? {
            children: [
              { path: '/', redirect: `/admin/${DEFAULT_ADMIN_SECTION}` },
              ...ADMIN_SECTION_NAMES.map((section): Route => ({
                path: '/' + section,
                action: () => renderAdminSection(section),
              })),
              { path: '(.*)', redirect: `/admin/${DEFAULT_ADMIN_SECTION}` },
            ],
          }
        : {}),
    })),
    { path: '(.*)', redirect: '/tasks' },
  ]);

  // Keep the bar title and the drawer's active item in sync with the route.
  function syncActive(): void {
    // First segment only — `/admin/people` is still the "admin" destination.
    const name = (location.pathname.split('/')[1] || 'tasks') as TabName;
    // Left Admin entirely: the next visit rebuilds the shell rather than
    // showing whatever its cards last loaded.
    if (name !== 'admin') adminMounted = false;
    title.textContent = ROUTES.find((r) => r.name === name)?.title() ?? '';
    for (const [n, item] of itemByName) {
      const active = n === name;
      item.classList.toggle('nav-active', active);
      if (active) item.setAttribute('aria-current', 'page');
      else item.removeAttribute('aria-current');
    }
  }
  window.addEventListener('vaadin-router-location-changed', syncActive);
  syncActive();
}

void boot();
